import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast } from "../../utils/toastUtils";
import { 
  FiCheckCircle, 
  FiClock, 
  FiXCircle, 
  FiSearch, 
  FiRefreshCw, 
  FiCreditCard,
  FiPhone
} from "react-icons/fi";
import { ToastContainer } from "react-toastify";

export default function ServicePaymentHistory() {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({
    totalCount: 0,
    paidCount: 0,
    paidAmount: 0,
    pendingCount: 0,
    failedCount: 0,
  });

  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      const res = await makeRequestAuth("post", "/admin/getServicePaymentHistory", {
        status: statusFilter,
      });

      if (!res.error && res.data) {
        setPayments(res.data.payments || []);
        if (res.data.stats) {
          setStats(res.data.stats);
        }
      } else {
        showErrorToast(res.message || "Failed to load payment history");
      }
    } catch (err) {
      showErrorToast("Something went wrong loading payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentHistory();
  }, [statusFilter]);

  // Client-side search filtering
  const filteredPayments = payments.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = item.userName?.toLowerCase().includes(searchLower);
    const phoneMatch = item.userPhone?.toLowerCase().includes(searchLower);
    const emailMatch = item.userEmail?.toLowerCase().includes(searchLower);
    const orderMatch = item.razorpayOrderId?.toLowerCase().includes(searchLower);
    const paymentMatch = item.razorpayPaymentId?.toLowerCase().includes(searchLower);
    const bookingMatch = item.bookingCustomId?.toString().toLowerCase().includes(searchLower);

    return nameMatch || phoneMatch || emailMatch || orderMatch || paymentMatch || bookingMatch;
  });

  // Date Formatter
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      <ToastContainer />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tight flex items-center gap-2">
            <FiCreditCard className="text-blue-600" /> Service Payment History
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Track all home service bookings payment transactions, success rates & pending statuses.
          </p>
        </div>
        <button
          onClick={fetchPaymentHistory}
          className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition w-fit cursor-pointer"
        >
          <FiRefreshCw className={loading ? "animate-spin" : ""} /> Refresh Data
        </button>
      </div>

      {/* Summary Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        {/* Paid / Success Card */}
        <div className="bg-white p-5 rounded-2xl border border-emerald-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Paid / Successful Revenue</p>
            <h3 className="text-2xl font-black text-emerald-600 mt-1">
              ₹{stats.paidAmount?.toLocaleString("en-IN") || 0}
            </h3>
            <p className="text-emerald-500 text-xs font-semibold mt-1">
              {stats.paidCount} Successful Payments
            </p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl">
            <FiCheckCircle />
          </div>
        </div>

        {/* Pending Card */}
        <div className="bg-white p-5 rounded-2xl border border-amber-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Pending Payments</p>
            <h3 className="text-2xl font-black text-amber-600 mt-1">
              {stats.pendingCount}
            </h3>
            <p className="text-amber-500 text-xs font-semibold mt-1">
              Awaiting User Verification
            </p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl">
            <FiClock />
          </div>
        </div>

        {/* Failed Card */}
        <div className="bg-white p-5 rounded-2xl border border-rose-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Failed Transactions</p>
            <h3 className="text-2xl font-black text-rose-600 mt-1">
              {stats.failedCount}
            </h3>
            <p className="text-rose-500 text-xs font-semibold mt-1">
              Cancelled or Declined
            </p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center text-xl">
            <FiXCircle />
          </div>
        </div>
      </div>

      {/* Main Content Area: Filters + Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Controls Header */}
        <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50/50">
          {/* Status Filter Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto">
            {[
              { id: "all", label: "All Payments" },
              { id: "paid", label: "Paid" },
              { id: "pending", label: "Pending" },
              { id: "failed", label: "Failed" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap cursor-pointer ${
                  statusFilter === tab.id
                    ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                    : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-72">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search user, phone, IDs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-xs font-semibold bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Transactions Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center text-slate-400 font-bold flex items-center justify-center gap-2">
              <FiRefreshCw className="animate-spin text-blue-600 text-lg" /> Loading payment records...
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-bold">
              No payment records found matching your filters.
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-100/70 border-b border-slate-200 text-[11px] font-black uppercase text-slate-500 tracking-wider">
                  <th className="py-3 px-4">Booking Info</th>
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4">Service</th>
                  <th className="py-3 px-4">Razorpay Ref</th>
                  <th className="py-3 px-4">Amount</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-semibold text-slate-700">
                {filteredPayments.map((item) => (
                  <tr key={item._id} className="hover:bg-slate-50/80 transition">
                    <td className="py-3.5 px-4 font-mono font-bold text-blue-600">
                      #{item.bookingCustomId || item._id?.toString().slice(-6)}
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-900">{item.userName}</span>
                        <span className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                          <FiPhone className="text-[10px]" /> {item.userPhone}
                        </span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 font-bold text-slate-800">
                      {item.serviceTitle}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[11px]">
                      <div className="flex flex-col">
                        <span className="text-slate-700 font-bold">{item.razorpayPaymentId || "-"}</span>
                        <span className="text-slate-400 text-[10px]">Order: {item.razorpayOrderId || "-"}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 font-black text-slate-900 text-sm">
                      ₹{item.amount?.toLocaleString("en-IN") || 0}
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          item.paymentStatus === "paid"
                            ? "bg-emerald-100 text-emerald-700"
                            : item.paymentStatus === "failed"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {item.paymentStatus === "paid" && <FiCheckCircle />}
                        {item.paymentStatus === "failed" && <FiXCircle />}
                        {item.paymentStatus === "pending" && <FiClock />}
                        {item.paymentStatus}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-slate-500 font-medium whitespace-nowrap">
                      {formatDate(item.paidAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
